import { Button, Box } from "@mui/material";
import { useCustomMediaQuery } from "@unit/hooks/src/use-custom-media";
import { SVGImgInitialTitle } from "@unit/svgs";
import NextLink from "next/link";
import React, { useState } from "react";

import PrimaryForm from "@/components/common/PrimaryForm";
import {
  authFormContainer,
  authFormSections,
  authHeaderSection,
  authLinkStyled,
  authLogoWrapper,
  authPanel,
  authSubmitButton,
  authSubtitle,
  authTitle,
} from "@/components/page-auth/auth-page-styles";
import OtpForm from "@/components/page-auth/OtpForm";
import { useAuthentication } from "@/custom-hooks/use-authentication";
import { useRedirectIfAuthenticated } from "@/custom-hooks/use-redirect-if-authenticated";
import { passwordResetPath } from "@/global-state/agency-app-path";

import type { OtpDataType } from "@/types/auth-otp";

const LoginContainer: React.FC = () => {
  const { isMobile } = useCustomMediaQuery();
  const { authLoginWithEmail, verifyUserMFA } = useAuthentication();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // 2段階認証が有効なスタッフは SMS を送れた時点でここに入り、認証コード入力へ切り替わる。
  // resolver は直列化できないためメモリ上だけで持ち、リロードしたら最初からやり直してもらう
  const [otpData, setOtpData] = useState<OtpDataType>();

  useRedirectIfAuthenticated();

  const isLoginDisabled = !email.trim() || !password.trim();

  const onSubmitEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoginDisabled) return;
    await authLoginWithEmail(email.trim(), password, (v) => setOtpData(v));
  };

  const onRestartOtp = () => {
    setOtpData(undefined);
    setPassword("");
  };

  if (otpData) {
    return (
      <Box css={authPanel} px={isMobile ? 2 : 3}>
        <div id="recaptcha-container" />
        <OtpForm onVerify={(code) => verifyUserMFA(otpData, code)} onRestart={onRestartOtp} />
      </Box>
    );
  }

  return (
    <Box css={authPanel} px={isMobile ? 2 : 3}>
      {/* invisible reCAPTCHA の置き場。SMS 送信より前に DOM に居る必要がある */}
      <div id="recaptcha-container" />
      <Box css={authFormContainer}>
        <Box component="form" css={authFormSections} onSubmit={onSubmitEmailAuth}>
          <Box css={authHeaderSection}>
            <Box css={authLogoWrapper}>
              <SVGImgInitialTitle />
            </Box>
            <Box css={authTitle} pb={1}>
              代理店ダッシュボード
            </Box>
            <Box css={authSubtitle}>以下の情報を入力しログインを完了してください。</Box>
          </Box>

          <Box>
            <Box pb={2.5}>
              <PrimaryForm
                type="email"
                variant="auth"
                required
                placeholder="hello@unit-g.com"
                title="メールアドレス"
                value={email}
                error={false}
                onChange={setEmail}
              />
            </Box>
            <PrimaryForm
              type="password"
              variant="auth"
              required
              title="パスワード"
              value={password}
              error={false}
              onChange={setPassword}
            />
          </Box>

          <Box>
            <Button
              css={authSubmitButton}
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoginDisabled}
            >
              ログイン
            </Button>
          </Box>

          <Box css={authLinkStyled}>
            <NextLink passHref href={passwordResetPath}>
              パスワードをお忘れですか？
            </NextLink>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default LoginContainer;
